import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import BrandShell from "../components/BrandShell";
import SEO from "../components/SEO";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Gift, Sparkles, Users, Trophy, RotateCcw, PartyPopper, Share2, Copy, Check } from "lucide-react";

const roleClass = (r) => r === "creator" ? "text-nexus-pink" : "text-nexus-blue";

function PoolTab({ v, l, active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`raffle-pool-${v}`}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${active ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white"}`}
    >
      {l}
    </button>
  );
}

function Confetti({ show }) {
  if (!show) return null;
  const pieces = Array.from({ length: 40 });
  return (
    <div className="pointer-events-none fixed inset-0 z-40 overflow-hidden">
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 400;
        const dur = 2200 + Math.random() * 1400;
        const hue = ["#FF2F6E", "#8A5CFF", "#4EA1FF", "#FFD84C"][i % 4];
        const size = 6 + Math.random() * 8;
        return (
          <div
            key={i}
            className="absolute rounded-sm"
            style={{
              left: `${left}%`,
              top: "-20px",
              width: size,
              height: size * 0.5,
              backgroundColor: hue,
              animation: `raffleFall ${dur}ms ${delay}ms linear forwards`,
              transform: `rotate(${Math.random() * 360}deg)`,
            }}
          />
        );
      })}
      <style>{`@keyframes raffleFall { to { transform: translateY(110vh) rotate(720deg); opacity: 0.4 } }`}</style>
    </div>
  );
}

export default function AdminRaffle() {
  const nav = useNavigate();
  const [pool, setPool] = useState("all");
  const [prize, setPrize] = useState("");
  const [entrants, setEntrants] = useState(null);
  const [busy, setBusy] = useState(false);
  const [winner, setWinner] = useState(null);
  const [history, setHistory] = useState([]);
  const [confetti, setConfetti] = useState(false);
  const [copied, setCopied] = useState(false);
  const [reelName, setReelName] = useState(null); // currently-shown name during the suspense reel
  const [reelPhase, setReelPhase] = useState("idle"); // idle | spinning | landed

  const loadEntrants = async (p = pool) => {
    try {
      const r = await api.get(`/admin/raffle/entrants?pool=${p}`);
      setEntrants(r.data);
    } catch (e) {
      const code = e.response?.status;
      if (code === 403) { toast.error("Admin only"); nav("/"); return; }
      if (code === 401) { toast.error("Log in first"); nav("/auth?mode=login"); return; }
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const loadHistory = async () => {
    try {
      const r = await api.get("/admin/raffle/history?limit=10");
      setHistory(r.data?.draws || []);
    } catch { /* silent */ }
  };

  useEffect(() => { loadEntrants(); loadHistory(); }, []);

  const setPoolAndReload = (v) => { setPool(v); loadEntrants(v); };

  // Slot-machine style reel: rapidly cycle through candidate names for ~4.5s
  // with a decelerating cadence, then land on the actual winner.
  const runReelAnimation = (candidates, finalName) => {
    return new Promise((resolve) => {
      if (!candidates || candidates.length === 0) { resolve(); return; }
      const pool = candidates.map((c) => c.name).filter(Boolean);
      // Ensure the final name is definitely in the pool for the last frames
      if (finalName && !pool.includes(finalName)) pool.push(finalName);
      // Schedule ~30 frames with a slow-in-fast-out-then-slow-again feel
      const frames = [];
      const total = 4500;
      let t = 0;
      // Fast phase: 80ms interval for first 2s
      while (t < 2000) { frames.push(80); t += 80; }
      // Slowing phase: interval grows from 90ms to 500ms over ~2.5s
      let step = 90;
      while (t < total) { frames.push(step); t += step; step = Math.min(520, step + 32); }

      let i = 0;
      setReelPhase("spinning");
      const tick = () => {
        if (i >= frames.length) {
          // Final landing frame: show the winner
          setReelName(finalName);
          setReelPhase("landed");
          resolve();
          return;
        }
        // Weight toward the final name in the last 6 frames
        const isFinalStretch = i >= frames.length - 6;
        const pick = isFinalStretch
          ? (Math.random() < 0.55 ? finalName : pool[Math.floor(Math.random() * pool.length)])
          : pool[Math.floor(Math.random() * pool.length)];
        setReelName(pick);
        setTimeout(tick, frames[i]);
        i += 1;
      };
      tick();
    });
  };

  const draw = async () => {
    setBusy(true);
    setWinner(null);
    setReelName(null);
    setReelPhase("idle");
    try {
      // Kick off both in parallel: the API call decides the actual winner,
      // the reel animation shows suspense on top.
      const drawPromise = api.post("/admin/raffle/draw", { pool, prize: prize.trim() || "Launch prize" });
      // Give the API a beat to respond so we can weight the reel toward the real winner.
      const r = await drawPromise;
      const finalName = r.data?.winner?.name || "";
      await runReelAnimation(entrants?.preview || [], finalName);
      // Brief hold on the winner name before revealing the full card
      await new Promise((res) => setTimeout(res, 600));
      setWinner(r.data);
      setConfetti(true);
      setTimeout(() => setConfetti(false), 4500);
      loadHistory();
      loadEntrants();
      toast.success(`🎉 ${r.data.winner.name} wins!`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setReelPhase("idle");
    } finally { setBusy(false); }
  };

  const clearWinner = () => { setWinner(null); setReelName(null); setReelPhase("idle"); };

  return (
    <BrandShell title="Admin · Raffle">
      <SEO title="Admin · Raffle · Nexus" description="Draw a launch-day raffle winner." path="/admin/raffle" />
      <Confetti show={confetti} />
      <div className="p-6 lg:p-10 space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2">
              <Gift className="h-3.5 w-3.5 text-nexus-pink" /> Admin only
            </div>
            <h1 className="font-display font-bold text-3xl tracking-tighter">Launch raffle</h1>
            <p className="text-zinc-400 mt-1.5 text-sm">Pick one random winner from everyone signed up right now. Perfect for your launch event giveaway.</p>
          </div>
        </div>

        {/* Entrants + Draw panel */}
        <div className="glass rounded-3xl p-6 md:p-8 space-y-6">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-xs uppercase tracking-widest text-zinc-500">Pool</div>
            <div className="flex gap-1" data-testid="raffle-pool-tabs">
              <PoolTab v="all" l="Everyone" active={pool === "all"} onClick={() => setPoolAndReload("all")} />
              <PoolTab v="creators" l="Creators" active={pool === "creators"} onClick={() => setPoolAndReload("creators")} />
              <PoolTab v="brands" l="Brands" active={pool === "brands"} onClick={() => setPoolAndReload("brands")} />
              <PoolTab v="today" l="Joined today" active={pool === "today"} onClick={() => setPoolAndReload("today")} />
              <PoolTab v="event" l="London event" active={pool === "event"} onClick={() => setPoolAndReload("event")} />
            </div>
            <div className="ml-auto flex items-center gap-2 text-sm">
              <Users className="h-4 w-4 text-zinc-400" />
              <span className="text-zinc-400">Eligible:</span>
              <span className="font-display font-bold text-2xl tabular-nums nexus-gradient-text" data-testid="raffle-eligible-count">
                {entrants ? entrants.total : "—"}
              </span>
            </div>
          </div>

          {entrants?.sources?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 text-[11px]" data-testid="raffle-sources">
              {entrants.sources.map((s) => (
                <span key={s.source} className={`px-2 py-1 rounded-full border ${s.source.startsWith("event") ? "border-nexus-pink/40 text-nexus-pink bg-nexus-pink/10" : "border-white/10 text-zinc-400"}`}>{s.source} · {s.count}</span>
              ))}
            </div>
          )}

          <Input
            value={prize}
            onChange={(e) => setPrize(e.target.value)}
            placeholder="Prize name (e.g. AirPods Pro, £100 Amazon voucher)"
            className="bg-white/[0.02] border-white/10 text-white"
            data-testid="raffle-prize-input"
          />

          <div className="flex items-center gap-3 flex-wrap">
            <Button
              onClick={draw}
              disabled={busy || !entrants || entrants.total === 0}
              className="nexus-gradient text-white border-0 font-semibold rounded-full h-12 px-8 text-base disabled:opacity-50"
              data-testid="raffle-draw-btn"
            >
              {busy ? (
                <><Sparkles className="h-4 w-4 mr-2 animate-spin" /> Drawing…</>
              ) : (
                <><Gift className="h-4 w-4 mr-2" /> Draw a winner</>
              )}
            </Button>
            {winner && (
              <Button
                onClick={clearWinner}
                variant="outline"
                className="rounded-full border-white/10 text-white hover:bg-white/5"
                data-testid="raffle-reset-btn"
              >
                <RotateCcw className="h-4 w-4 mr-2" /> Draw again
              </Button>
            )}
          </div>

          {entrants && entrants.total === 0 && (
            <p className="text-sm text-zinc-500">No entrants in this pool yet — invite people to sign up first.</p>
          )}
        </div>

        {/* Suspense reel — shown while the winner is being "chosen" */}
        {reelPhase !== "idle" && (
          <div
            className="glass rounded-3xl p-8 md:p-12 relative overflow-hidden border-2 border-nexus-purple/40"
            data-testid="raffle-reel"
          >
            <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-nexus-purple/20 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full bg-nexus-pink/20 blur-3xl pointer-events-none" />
            <div className="relative text-center">
              <div className="text-xs uppercase tracking-[0.4em] text-zinc-400 mb-4 flex items-center justify-center gap-2">
                {reelPhase === "spinning" ? (
                  <><Sparkles className="h-3.5 w-3.5 text-nexus-pink animate-spin" /> Picking a winner…</>
                ) : (
                  <><Trophy className="h-3.5 w-3.5 text-nexus-pink" /> And the winner is…</>
                )}
              </div>
              <div
                className={`font-display font-black tracking-tighter transition-all duration-300 ${
                  reelPhase === "landed"
                    ? "text-6xl md:text-8xl nexus-gradient-text drop-shadow-[0_0_60px_rgba(255,47,110,0.5)]"
                    : "text-4xl md:text-6xl text-white/90 blur-[1px]"
                }`}
                data-testid="raffle-reel-name"
                style={{
                  animation: reelPhase === "spinning" ? "reelPulse 120ms infinite alternate" : undefined,
                }}
              >
                {reelName || "..."}
              </div>
              {reelPhase === "spinning" && (
                <div className="mt-6 flex justify-center gap-1.5">
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      className="h-1.5 w-1.5 rounded-full bg-nexus-pink"
                      style={{ animation: `reelDot 1s ${i * 0.15}s infinite ease-in-out` }}
                    />
                  ))}
                </div>
              )}
            </div>
            <style>{`
              @keyframes reelPulse { 0% { opacity: 0.85; transform: scale(1) } 100% { opacity: 1; transform: scale(1.015) } }
              @keyframes reelDot { 0%, 100% { opacity: 0.3; transform: translateY(0) } 50% { opacity: 1; transform: translateY(-4px) } }
            `}</style>
          </div>
        )}

        {/* Winner card */}
        {winner && (
          <div className="glass rounded-3xl p-8 md:p-10 relative overflow-hidden border-2 border-nexus-pink/40" data-testid="raffle-winner-card">
            <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-nexus-pink/20 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-nexus-pink mb-3">
                <Trophy className="h-3.5 w-3.5" /> Winner
              </div>
              <div className="flex items-start gap-6 flex-wrap">
                {winner.winner.avatar_url ? (
                  <img src={winner.winner.avatar_url} alt="" className="w-24 h-24 rounded-2xl object-cover shrink-0" />
                ) : (
                  <div className="w-24 h-24 rounded-2xl nexus-gradient flex items-center justify-center font-display font-black text-white text-5xl shrink-0">
                    {(winner.winner.name || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-display font-black text-4xl md:text-5xl nexus-gradient-text tracking-tighter" data-testid="raffle-winner-name">
                    {winner.winner.name}
                  </div>
                  <div className={`text-sm mt-1 uppercase tracking-widest font-semibold ${roleClass(winner.winner.role)}`}>
                    {winner.winner.role} {winner.winner.company ? `· ${winner.winner.company}` : ""}
                  </div>
                  {winner.winner.tagline && <div className="text-sm text-zinc-300 mt-2 italic">&ldquo;{winner.winner.tagline}&rdquo;</div>}
                  {(winner.winner.niches || []).length > 0 && (
                    <div className="flex gap-1.5 mt-2 flex-wrap">
                      {winner.winner.niches.slice(0, 4).map((n) => (
                        <span key={n} className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-zinc-300">{n}</span>
                      ))}
                    </div>
                  )}
                  <div className="mt-4 flex items-center gap-3 flex-wrap text-sm">
                    <a href={`mailto:${winner.winner.email}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] hairline text-nexus-blue hover:bg-white/[0.08]" data-testid="raffle-winner-email">
                      {winner.winner.email}
                    </a>
                    {winner.winner.location && <span className="text-zinc-500 text-xs">📍 {winner.winner.location}</span>}
                  </div>
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                    <div className="p-3 rounded-xl bg-white/[0.02] hairline"><div className="text-[10px] text-zinc-500 uppercase tracking-widest">Prize</div><div className="font-semibold mt-0.5">{winner.prize}</div></div>
                    <div className="p-3 rounded-xl bg-white/[0.02] hairline"><div className="text-[10px] text-zinc-500 uppercase tracking-widest">Pool</div><div className="font-semibold mt-0.5">{winner.pool} ({winner.pool_size})</div></div>
                    <div className="p-3 rounded-xl bg-white/[0.02] hairline"><div className="text-[10px] text-zinc-500 uppercase tracking-widest">Drawn</div><div className="font-semibold mt-0.5">{new Date(winner.drawn_at).toLocaleString("en-GB")}</div></div>
                  </div>

                  {/* Share panel */}
                  {(() => {
                    const shareUrl = `${window.location.origin}/win/${winner.id}`;
                    const shareText = `${winner.winner.name} just won ${winner.prize} on Nexus 🎉 collabnexus.co.uk`;
                    const copyLink = async () => {
                      try {
                        await navigator.clipboard.writeText(shareUrl);
                        setCopied(true);
                        toast.success("Link copied");
                        setTimeout(() => setCopied(false), 1800);
                      } catch { toast.error("Copy failed"); }
                    };
                    return (
                      <div className="mt-5 p-4 rounded-2xl bg-white/[0.03] border border-nexus-pink/20" data-testid="raffle-share-panel">
                        <div className="text-[10px] uppercase tracking-widest text-nexus-pink flex items-center gap-1.5 mb-3">
                          <Share2 className="h-3 w-3" /> Share this win
                        </div>
                        <div className="flex items-center gap-2 mb-3">
                          <input readOnly value={shareUrl} className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-xs text-zinc-300 font-mono" data-testid="raffle-share-url" onClick={(e) => e.target.select()} />
                          <Button onClick={copyLink} className="rounded-lg bg-white/10 text-white hover:bg-white/20 h-9 px-3" data-testid="raffle-copy-btn">
                            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <a
                            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                            target="_blank" rel="noreferrer"
                            className="text-xs px-3 py-1.5 rounded-full bg-black text-white hover:opacity-80"
                            data-testid="raffle-share-x"
                          >Share on X</a>
                          <a
                            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                            target="_blank" rel="noreferrer"
                            className="text-xs px-3 py-1.5 rounded-full bg-[#0A66C2] text-white hover:opacity-80"
                            data-testid="raffle-share-linkedin"
                          >LinkedIn</a>
                          <a
                            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                            target="_blank" rel="noreferrer"
                            className="text-xs px-3 py-1.5 rounded-full bg-[#1877F2] text-white hover:opacity-80"
                            data-testid="raffle-share-facebook"
                          >Facebook</a>
                          <a
                            href={shareUrl}
                            target="_blank" rel="noreferrer"
                            className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white hover:bg-white/10"
                            data-testid="raffle-share-preview"
                          >Open card ↗</a>
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <PartyPopper className="h-10 w-10 text-nexus-pink hidden md:block" />
              </div>
            </div>
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <div className="glass rounded-2xl p-6" data-testid="raffle-history">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Previous draws</div>
            <div className="space-y-2">
              {history.map((d) => (
                <div key={d.id} className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02] hairline text-sm">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{d.winner?.name} <span className="text-[10px] text-zinc-500 uppercase ml-1">{d.winner?.role}</span></div>
                    <div className="text-[10px] text-zinc-500 truncate">{d.winner?.email} · {d.prize}</div>
                  </div>
                  <div className="text-[10px] text-zinc-600 shrink-0">{new Date(d.drawn_at).toLocaleString("en-GB")}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </BrandShell>
  );
}
